import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./SideNav.css";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupsIcon from "@mui/icons-material/Groups";
import SchoolIcon from "@mui/icons-material/School";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import DnsIcon from "@mui/icons-material/Dns";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import configData from "../configure";
import egurukulappslogo from "../assets/logo.png";

import { useAuth } from "../AuthContext";

const isRouteActive = (pathname, route) => {
  if (!pathname || !route) return false;
  const p = pathname.toLowerCase();
  const r = route.toLowerCase();
  return p === r || p.startsWith(`${r}/`);
};

const SideNav = React.memo(({ mobileOpen, onClose, onCollapseChange }) => {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = window.localStorage.getItem("sidenav:collapsed");
    return saved === "true";
  });
  const [expandedMenus, setExpandedMenus] = useState({});

  const location = useLocation();
  const { userRole, user, orgId, axios, token, parentOrgId } = useAuth();
  const effectiveOrgId = orgId || user?.orgId || user?.organizationId || user?.org_id || null;
  const effectiveParentOrgId = parentOrgId || user?.parentOrgId || user?.orgDetails?.parentOrgId || '';
  const isSmallOrg = effectiveParentOrgId === 'SMALLORG-001';

  const isAdmin = userRole === "ADMIN";
  const isFaculty = userRole === "FACULTY";
  const isStaff = userRole === "STAFF";
  const isStudent = userRole === "STUDENT";

  const dashboardPath = isStudent
    ? "/student-dashboard"
    : isFaculty
      ? "/faculty-dashboard"
      : "/admin-dashboard";

  const menuItems = useMemo(() => {
    const items = [
      {
        key: "dashboard",
        label: "Dashboard",
        icon: <DashboardIcon className="navIcon" />,
        to: dashboardPath,
      },
      ...(isStudent
        ? [
          {
            key: "profile",
            label: "My Profile",
            icon: <PersonOutlinedIcon className="navIcon" />,
            to: "/Students",
          },
          {
            key: "inbox",
            label: "Inbox",
            icon: <CircleNotificationsIcon className="navIcon" />,
            to: "/student-inbox",
          },
          {
            key: "classes",
            label: "Classes",
            icon: <SchoolIcon className="navIcon" />,
            to: "/classes",
          },
          ...(!isSmallOrg
            ? [
              {
                key: "courses",
                label: "Courses",
                icon: <SchoolOutlinedIcon className="navIcon" />,
                to: "/courses",
              },
            ]
            : []),
          ...(!isSmallOrg
            ? [
              {
                key: "exams",
                label: "Examinations",
                icon: <SchoolOutlinedIcon className="navIcon" />,
                to: "/exams",
              },
            ]
            : []),
          {
            key: "attendance",
            label: "My Attendance",
            icon: <CalendarMonthIcon className="navIcon" />,
            to: "/attendance/student",
          },
        ]
        : [
          {
            key: "users",
            label: "Users",
            icon: <GroupsIcon className="navIcon" />,
            children: [
              { key: "students", label: "Students", to: "/Students" },
              ...(isAdmin || isFaculty ? [{ key: "faculty", label: "Faculty", to: "/Faculty" }] : []),
              ...(!isSmallOrg ? [{ key: "staff", label: "Staff", to: "/Staff" }] : []),
              { key: "admin", label: "Admin", to: "/Admin" },
            ],
          },
          ...((isAdmin || isFaculty) && effectiveOrgId === "62d3b1f4-e453-4933-bfcf-7b5adf5b4e83"
            ? [
              {
                key: "kv-faculty",
                label: "KV Operations",
                icon: <SchoolIcon className="navIcon" />,
                to: "/kv-faculty",
              },
            ]
            : []),
          {
            key: "academics",
            label: "Academics",
            icon: <SchoolIcon className="navIcon" />,
            children: [
              { key: "classes", label: "Classes", to: "/classes" },
              { key: "courses", label: "Courses", to: "/courses" },
              ...(isSmallOrg
                ? [{ key: "schedules", label: "Schedules", to: "/academics/schedules" }]
                : []),
            ],
          },
          ...((isAdmin || isFaculty) && !isSmallOrg
            ? [
              {
                key: "ai",
                label: "AI Assistant",
                icon: <SmartToyIcon className="navIcon" />,
                children: [
                  { key: "faculty-ai", label: "Faculty Assistant", to: "/Faculty-Assistant" },
                  { key: "local-ai", label: "Local AI Agents", to: "/local-ai-agents" },
                  { key: "doc-ai", label: "Document AI", to: "/doc-ai-assistant" },
                ],
              },
            ]
            : []),
          ...(!isSmallOrg
            ? [
              {
                key: "examinations",
                label: "Examinations",
                icon: <SchoolOutlinedIcon className="navIcon" />,
                children: [
                  { key: "manage-exams", label: "Exams", to: "/exams" },
                  { key: "performance", label: "Performance", to: "/performance" },
                ],
              },
            ]
            : []),
          {
            key: "attendance",
            label: "Attendance",
            icon: <CalendarMonthIcon className="navIcon" />,
            children: [
              { key: "attendance-mark", label: "Mark Attendance", to: "/attendance/mark" },
              { key: "attendance-register", label: "Attendance Register", to: "/attendance/register" },
              ...((isAdmin || isFaculty) && effectiveOrgId === "62d3b1f4-e453-4933-bfcf-7b5adf5b4e83" ? [
                { key: "attendance-center-visits", label: "Center Visits", to: "/attendance/center-visits" }
              ] : [])
            ],
          },
        ]),
      {
        key: "events",
        label: "Events",
        icon: <WidgetsOutlinedIcon className="navIcon" />,
        to: "/events",
      },
      ...((isAdmin || isFaculty || isStaff) && !isStudent
        ? [
          {
            key: "erp-inbox",
            label: "Inbox Messages",
            icon: <CircleNotificationsIcon className="navIcon" />,
            to: "/erp-inbox",
          },
        ]
        : []),
      ...(isAdmin
        ? [
          {
            key: "finance",
            label: "Finance",
            icon: <AccountBalanceIcon className="navIcon" />,
            children: [
              { key: "fee-planning", label: "Fee Planning", to: "/finance" },
              { key: "student-payments", label: "Student Payments", to: "/student-payments" },
              ...(!isSmallOrg ? [{ key: "fee-analytics", label: "Fee Analytics", to: "/feeanalytics" }] : []),
            ],
          },
          {
            key: "settings",
            label: "Settings",
            icon: <SettingsOutlinedIcon className="navIcon" />,
            to: "/settings",
          },
          {
            key: "analytics",
            label: "Analytics",
            icon: <AnalyticsOutlinedIcon className="navIcon" />,
            to: "/analytics",
          },
          ...(!isSmallOrg
            ? [
              {
                key: "transportation",
                label: "Transportation",
                icon: <DirectionsBusIcon className="navIcon" />,
                to: "/transportation",
              },
              {
                key: "services-status",
                label: "Service Status",
                icon: <DnsIcon className="navIcon" />,
                to: "/services-status",
              },
            ]
            : []),
        ]
        : []),
    ];

    // Filter items based on OrgId
    const restrictedOrgId = "62d3b1f4-e453-4933-bfcf-7b5adf5b4e83";
    const filteredItems = items.filter(item => {
      if (orgId === restrictedOrgId) {
        if (item.key === "finance" || item.key === "transportation") {
          return false;
        }
      }
      return true;
    });

    return filteredItems;
  }, [dashboardPath, isAdmin, isFaculty, isStaff, isStudent, orgId, effectiveOrgId, isSmallOrg]);

  useEffect(() => {
    onCollapseChange?.(collapsed);
    window.localStorage.setItem("sidenav:collapsed", String(collapsed));
  }, [collapsed, onCollapseChange]);

  useEffect(() => {
    // Only auto-expand if we're not already expanded for this item
    // and avoid unnecessary updates if current pathname hasn't moved into a new parent
    let changed = false;
    const next = { ...expandedMenus };

    menuItems.forEach((item) => {
      if (item.children?.some((child) => isRouteActive(location.pathname, child.to))) {
        if (!next[item.key]) {
          next[item.key] = true;
          changed = true;
        }
      }
    });

    if (changed) {
      setExpandedMenus(next);
    }
  }, [location.pathname, menuItems]); // eslint-disable-line react-hooks/exhaustive-deps



  const setFlyoutPosition = (e) => {
    if (!mobileOpen) {
      const rect = e.currentTarget.getBoundingClientRect();
      const flyoutHeight = 160;
      let top = rect.top;
      if (top + flyoutHeight > window.innerHeight - 8) {
        top = Math.max(8, window.innerHeight - flyoutHeight - 8);
      }
      e.currentTarget.style.setProperty("--item-top", `${top}px`);
    }
  };

  const handleCollapseToggle = () => {
    setCollapsed((prev) => !prev);
  };

  const handleLeafClick = () => {
    if (mobileOpen) {
      onClose?.();
    }
  };

  const handleParentToggle = (key) => {
    setExpandedMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {mobileOpen && <button className="sideNavBackdrop" onClick={onClose} aria-label="Close sidebar" />}

      <aside
        className={`sideNav ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}
      >
        <div className="wrapSideLogo">
          {collapsed ? (
            <div className="collapsed-org-icon">
              <img src={egurukulappslogo} alt="Logo" style={{ width: '28px', height: 'auto', objectFit: 'contain' }} />
            </div>
          ) : (
            <div className="org-name-brand" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px', height: '100%', overflow: 'hidden' }}>
              <img src={egurukulappslogo} alt="eGurukulApps Logo" style={{ width: '100%', maxWidth: '110px', maxHeight: '35px', height: 'auto', objectFit: 'contain' }} />
            </div>
          )}
          <button className="collapseBtn" onClick={handleCollapseToggle} aria-label="Toggle sidebar width">
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>
        </div>

        <nav className="navMenu" aria-label="Primary">
          {menuItems.map((item) => {
            if (item.to) {
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  className={({ isActive }) => `sideNavItem ${isActive ? "active" : ""}`}
                  onClick={handleLeafClick}
                  onMouseEnter={setFlyoutPosition}
                >
                  {item.icon}
                  {!collapsed && <span className="menuLabel">{item.label}</span>}
                  <span className="tooltip">{item.label}</span>
                </NavLink>
              );
            }

            const isParentActive = item.children?.some((child) => isRouteActive(location.pathname, child.to));
            const isExpanded = !!expandedMenus[item.key];

            return (
              <div
                key={item.key}
                className={`menuBlock ${isParentActive ? "parent-active" : ""}`}
                onMouseEnter={setFlyoutPosition}
              >
                <button
                  type="button"
                  className={`sideNavItem menuParent ${isExpanded ? "submenu-open" : ""}`}
                  aria-expanded={isExpanded}
                  aria-controls={`submenu-${item.key}`}
                  onClick={() => handleParentToggle(item.key)}
                >
                  {item.icon}
                  {!collapsed && <span className="menuLabel">{item.label}</span>}
                  {!collapsed && <ExpandMoreIcon className="menuArrow" />}
                  <span className="tooltip">{item.label}</span>
                </button>

                <div
                  id={`submenu-${item.key}`}
                  className={`sideSubmenu ${isExpanded && !collapsed ? "open" : ""}`}
                >
                  <div className="sideSubmenuInner">
                    {item.children?.map((child) => (
                      <NavLink
                        key={child.key}
                        to={child.to}
                        className={({ isActive }) => `sideNavItem sub ${isActive ? "active" : ""}`}
                        onClick={handleLeafClick}
                      >
                        <span className="subDot" />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

      </aside>
    </>
  );
});

export default SideNav;
